<template>
  <b-modal
    ref="modal"
    :title="$t(isEdit ? 'breadcrumb.update_holiday' : 'breadcrumb.create_holiday')"
    hide-footer
    @hidden="onHidden"
  >
    <validation-observer
      ref="formValidation"
      #default="{ invalid }"
    >
      <validation-provider
        v-slot="{ errors }"
        :name="$t('label.title')"
        :rules="
          locale === appLocale
            ? 'required|max:100'
            : 'max:100'
        "
        style="width: 100%;"
      >
        <b-form-group
          :label="$t('label.title')"
          :state="errors.length === 0 ? null : false"
          class="mb-3"
        >
          <div class="d-flex align-items-center">
            <div class="flex-grow-1 mr-1">
              <b-form-input
                v-model="formData.title[locale]"
                :placeholder="$t('label.title')"
                :state="errors.length === 0 ? null : false"
              />
            </div>
            <div style="min-width: 80px">
              <b-form-select
                v-model="locale"
                :options="locales"
                class="language-select"
              />
            </div>
          </div>
          <small class="text-danger">{{ errors[0] }}</small>
        </b-form-group>
      </validation-provider>

      <validation-provider
        v-slot="{ errors }"
        :name="$t('field.type')"
        rules="required"
      >
        <b-form-group
          :label="$t('field.type')"
          label-for="type"
        >
          <b-form-radio-group
            id="type"
            v-model="formData.isRepeatEveryYear"
            :options="[
              { text: $t('field.applyOnce'), value: false },
              { text: $t('field.repeatAnnually'), value: true }
            ]"
            :state="errors.length > 0 ? false : null"
          />
          <small class="text-danger">{{ errors[0] }}</small>
        </b-form-group>
      </validation-provider>

      <validation-provider
        v-slot="{ errors }"
        :name="$t('field.date')"
        rules="required"
      >
        <b-form-group
          :label="$t('field.date')"
          label-for="date"
        >
          <template v-if="!formData.isRepeatEveryYear">
            <b-form-datepicker
              id="date"
              v-model="formData.date"
              :locale="$i18n.locale"
              :date-format-options="{
                year: 'numeric',
                month: 'short',
                day: '2-digit'
              }"
              v-bind="labels[$i18n.locale] || {}"
              :min="minDate"
              weekday-header-format="narrow"
              :state="errors.length > 0 ? false : null"
            />
          </template>
          <template v-else>
            <month-day-picker
              v-model="formData.date"
              :state="errors.length > 0 ? false : null"
            />
          </template>
          <small class="text-danger">{{ errors[0] }}</small>
        </b-form-group>
      </validation-provider>

      <validation-provider
        v-slot="{ errors }"
        :name="$t('field.description')"
      >
        <b-form-group
          :label="$t('field.description')"
          label-for="description"
        >
          <b-form-textarea
            id="description"
            v-model="formData.description"
            :placeholder="$t('field.description')"
            rows="3"
            :state="errors.length > 0 ? false : null"
          />
          <small class="text-danger">{{ errors[0] }}</small>
        </b-form-group>
      </validation-provider>

      <b-row class="mt-3">
        <b-col class="text-right">
          <b-button
            variant="outline-secondary"
            class="mr-2"
            @click="hide"
          >
            {{ $t('button.cancel') }}
          </b-button>
          <b-button
            variant="primary"
            :disabled="invalid || submitting || !formData.title[locale] || !formData.date"
            @click="onSubmit"
          >
            <span
              v-if="submitting"
              class="spinner-border spinner-border-sm mr-1"
            />
            {{ isEdit ? $t('button.update') : $t('button.save') }}
          </b-button>
        </b-col>
      </b-row>
    </validation-observer>
  </b-modal>
</template>

<script>
import {
  BModal,
  BForm,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BButton,
  BFormDatepicker,
  BRow,
  BCol,
  BFormRadioGroup,
  BFormTextarea,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import MonthDayPicker from '@/components/NMonthDayPicker.vue'
import labels from '@/data/localeDatepicker'

export default {
  name: 'HolidayFormModal',
  components: {
    BModal,
    BForm,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BButton,
    BFormDatepicker,
    BRow,
    BCol,
    BFormRadioGroup,
    BFormTextarea,
    ValidationProvider,
    ValidationObserver,
    MonthDayPicker,
  },
  props: {
    isEdit: {
      type: Boolean,
      default: false,
    },
    item: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      formData: {
        title: {
          en: '',
          zh: '',
          km: '',
          vi: '',
        },
        date: '',
        description: '',
        isRepeatEveryYear: false,
      },
      submitting: false,
      labels,
      locale: this.$i18n.locale.split('-')[0],
      locales: [
        { value: 'en', text: 'English' },
        { value: 'zh', text: '中文' },
        { value: 'km', text: 'ខ្មែរ' },
        { value: 'vi', text: 'Tiếng Anh' },
      ],
    }
  },
  computed: {
    minDate() {
      return new Date(new Date().setDate(new Date().getDate()))
    },
    appLocale() {
      return this.$i18n.locale.split('-')[0]
    }
  },
  watch: {
    '$i18n.locale'(val) {
      const [language] = val.split('-')
      this.locale = language
    },
    'formData.isRepeatEveryYear': function (newVal, oldVal) {
      if (newVal !== oldVal && this.formData.date) {
        const date = new Date(this.formData.date)
        if (newVal) {
          // When switching to repeat every year, keep only month and day
          const currentYear = new Date().getFullYear()
          this.formData.date = new Date(
            currentYear,
            date.getMonth(),
            date.getDate() + 1,
          )
            .toISOString()
            .split('T')[0]
        }
      }
    },
    item: {
      immediate: true,
      handler(newVal) {
        if (this.isEdit && newVal) {
          this.formData = {
            ...newVal,
            departmentId: newVal.departmentId || this.$route.params.departmentId,
          }
          
          // Handle title conversion for multi-language support
          if (newVal.title) {
            this.formData.title = this.convertTitleToObject(newVal.title);
          }
        }
      },
    },
  },
  methods: {
    // Helper method to convert string title to object
    convertTitleToObject(title) {
      if (typeof title === 'string') {
        const defaultTitle = title
        const titleObj = {
          en: defaultTitle,
          zh: '',
          km: '',
          vi: '',
        }
        // Set the current locale's value
        const currentLocale = this.appLocale
        if (['en', 'zh', 'km', 'vi'].includes(currentLocale)) {
          titleObj[currentLocale] = defaultTitle
        }
        return titleObj
      }
      return title
    },
    
    show() {
      if (this.isEdit && this.item) {
        this.formData = {
          ...this.item,
          departmentId: this.$route.params.departmentId,
        }
        
        // Handle title conversion for multi-language support
        if (this.item.title) {
          this.formData.title = this.convertTitleToObject(this.item.title);
        }
      } else {
        this.resetForm()
      }
      this.$refs.modal.show()
    },
    hide() {
      this.$refs.modal.hide()
    },
    onHidden() {
      this.resetForm()
      this.$emit('hide')
    },
    resetForm() {
      this.formData = {
        title: {
          en: '',
          zh: '',
          km: '',
          vi: '',
        },
        date: '',
        description: '',
        isRepeatEveryYear: false,
        departmentId: this.$route.params.departmentId || null,
      }
      this.$nextTick(() => {
        this.$refs.formValidation?.reset()
      })
    },
    async onSubmit() {
      const isValid = await this.$refs.formValidation.validate()

      if (!isValid || this.submitting) return

      this.submitting = true
      try {
        await this.$emit('submit', this.formData)
      } finally {
        this.submitting = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.modal-body {
  padding: 1.5rem;
}

.language-select {
  height: 100%;
}
</style>
